import type { JSX } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { EmptyState, FaultState } from './FaultState';
import { useWrite } from './MoneyForms';
import { useResource, refetchResource } from '../lib/store';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';
import {
  bonusTrackerKey, fetchBonusTracker, saveBonusMonth, saveBonusHoursMin, earnMonth, emptyMonth, money,
  bonusSendState,
  type BonusMonth, type BonusPlan,
} from '../lib/bonusTracker';
import { session } from '../lib/session';
import { submitManagerEntry, voidEntryLine } from '../lib/managerEntry';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const YEAR_INDEXES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const QUARTERS = [
  { id: 1, label: 'Q1 Jan–Mar', indexes: [0, 1, 2] },
  { id: 2, label: 'Q2 Apr–Jun', indexes: [3, 4, 5] },
  { id: 3, label: 'Q3 Jul–Sep', indexes: [6, 7, 8] },
  { id: 4, label: 'Q4 Oct–Dec', indexes: [9, 10, 11] },
];

function monthClass(i: number): string {
  const q = Math.floor(i / 3) + 1;
  return [
    'bonus-m',
    `bonus-m-q${q}`,
    i % 3 === 0 ? 'bonus-qstart' : '',
    i % 3 === 2 ? 'bonus-qend' : '',
  ].filter(Boolean).join(' ');
}

function asMonths(raw: BonusMonth[] | undefined): BonusMonth[] {
  const byMonth = new Map((raw || []).map((m) => [m.month, m]));
  return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => {
    const row = byMonth.get(n);
    return row ? { ...emptyMonth(n), ...row } : emptyMonth(n);
  });
}

function numFrom(raw: string): number | null {
  const t = raw.trim().replace(/%/g, '');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function sameMonth(a: BonusMonth, b: BonusMonth): boolean {
  return a.closingPct === b.closingPct
    && a.npsPct === b.npsPct
    && a.membersLast === b.membersLast
    && a.membersCurrent === b.membersCurrent
    && a.hoursLast === b.hoursLast
    && a.hoursCurrent === b.hoursCurrent
    && a.mondayDone === b.mondayDone
    && a.hiringOk === b.hiringOk
    && a.discretionary === b.discretionary
    && a.bestScore === b.bestScore
    && a.bestPaid === b.bestPaid
    && a.actualPaid === b.actualPaid
    && a.membersOverride === b.membersOverride
    && a.hoursOverride === b.hoursOverride
    && a.gsSalesPct === b.gsSalesPct;
}

export function BonusTracker({ clinic }: { clinic: string }): JSX.Element {
  const [year, setYear] = useState(2026);
  const data = useResource(bonusTrackerKey(year), () => fetchBonusTracker(year));
  if (data.status === 'loading') return <EmptyState label="Loading bonus tracker…" />;
  if (data.status === 'fault') return <FaultState name="could not load bonus tracker" detail={data.error} />;
  const plan = (data.data.plans || []).find((p) => p.locationCode === clinic);
  const years = data.data.years?.length ? data.data.years : [year];
  return (
    <BonusGrid
      key={`${clinic}-${year}`}
      clinic={clinic}
      year={year}
      years={years}
      onYear={setYear}
      managerName={plan?.managerName ?? null}
      hoursMin={plan?.hoursMin ?? 1700}
      canWrite={data.data.canWrite}
      months={asMonths(plan?.months)}
      plan={plan}
    />
  );
}

function BonusGrid(
  { clinic, year, years, onYear, managerName, hoursMin, canWrite, months: start, plan }:
  {
    clinic: string;
    year: number;
    years: number[];
    onYear: (year: number) => void;
    managerName: string | null;
    hoursMin: number;
    canWrite: boolean;
    months: BonusMonth[];
    plan?: BonusPlan;
  },
): JSX.Element {
  const [months, setMonths] = useState(start);
  const [savedMonths, setSavedMonths] = useState(start);
  const [floor, setFloor] = useState(hoursMin);
  const [savedFloor, setSavedFloor] = useState(hoursMin);
  const [editing, setEditing] = useState(false);
  const save = useWrite();
  const earned = useMemo(() => months.map((m) => earnMonth(m, floor)), [months, floor]);
  const yearEarned = earned.reduce((n, e) => n + e.total, 0);
  const yearPaid = months.reduce((n, m) => n + (m.actualPaid ?? 0), 0);
  const open = canWrite && editing;

  function patch(i: number, next: Partial<BonusMonth>): BonusMonth[] {
    return months.map((m, idx) => (idx === i ? { ...m, ...next } : m));
  }

  function onDraft(i: number, next: Partial<BonusMonth>): void {
    if (!open) return;
    setMonths(patch(i, next));
  }

  function startEdit(): void {
    setEditing(true);
  }

  function cancelEdit(): void {
    setMonths(savedMonths);
    setFloor(savedFloor);
    setEditing(false);
  }

  function saveAll(): void {
    if (!canWrite) return;
    const dirty = months.filter((m, i) => !sameMonth(m, savedMonths[i]));
    const floorDirty = floor !== savedFloor;
    if (dirty.length === 0 && !floorDirty) {
      setEditing(false);
      return;
    }
    void save.run(
      async () => {
        if (floorDirty) await saveBonusHoursMin(clinic, year, floor);
        for (const row of dirty) {
          // eslint-disable-next-line no-await-in-loop
          await saveBonusMonth(row, clinic, year);
        }
        await refetchResource(bonusTrackerKey(year), () => fetchBonusTracker(year));
        setSavedMonths(months);
        setSavedFloor(floor);
        setEditing(false);
      },
    );
  }

  return (
    <div class="card" data-testid="bonus-tracker">
      <div class="section-head">
        <div class="section-head-row">
          <h2>{year} Annual Bonus — {LOCATION_NAMES[clinic as LocationCode] ?? clinic}</h2>
          <div class="section-head-actions">
            {years.length > 1 ? (
              <label>
                Year{' '}
                <select
                  data-testid="bonus-year"
                  value={String(year)}
                  disabled={editing}
                  onChange={(e) => onYear(Number((e.target as HTMLSelectElement).value))}
                >
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </label>
            ) : null}
            <span class="bonus-ytd" data-testid="bonus-ytd">
              YTD Annual Bonus {money(yearPaid || yearEarned)}
            </span>
            {canWrite && !editing ? (
              <button type="button" class="btn" data-testid="bonus-edit" onClick={startEdit}>Edit</button>
            ) : null}
            {open ? (
              <>
                <button type="button" class="btn" data-testid="bonus-cancel" onClick={cancelEdit} disabled={save.busy}>
                  Cancel
                </button>
                <button type="button" class="btn primary" data-testid="bonus-save" onClick={saveAll} disabled={save.busy}>
                  {save.busy ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : null}
          </div>
        </div>
        <p>
          {managerName ? `${managerName}. ` : ''}
          Full calendar year.
          {' '}
          Service hours minimum{' '}
          {open ? (
            <input
              class="row-edit"
              inputMode="numeric"
              data-testid="bonus-hours-min"
              aria-label="Service hours minimum"
              value={String(floor)}
              onInput={(e) => {
                const n = Number((e.target as HTMLInputElement).value);
                if (Number.isInteger(n) && n >= 0 && n <= 5000) setFloor(n);
                if ((e.target as HTMLInputElement).value === '') setFloor(0);
              }}
            />
          ) : (
            <strong data-testid="bonus-hours-min">{floor.toLocaleString('en-US')}</strong>
          )}
          .
          {canWrite
            ? (editing
              ? ' Change what you need, then Save. Cancel throws the edits away.'
              : ' Click Edit to type figures. Managers can see this year.')
            : ' You can see every figure for the year. You cannot change them.'}
        </p>
      </div>
      {save.error ? <small class="bad" role="alert">{save.error}</small> : null}
      <div class="table-wrap">
        <table class="task-table bonus-table" data-testid="bonus-year-table">
          <thead>
            <tr>
              <th></th>
              {QUARTERS.map((q) => {
                const qEarned = q.indexes.reduce((n, i) => n + earned[i].total, 0);
                const qPaid = q.indexes.reduce((n, i) => n + (months[i].actualPaid ?? 0), 0);
                return (
                  <th
                    key={q.id}
                    colSpan={3}
                    class={`bonus-qhead bonus-m-q${q.id}`}
                    data-testid={`bonus-q${q.id}`}
                  >
                    {q.label}
                    {' '}
                    <span class="bonus-ytd" data-testid={`bonus-q${q.id}-total`}>
                      {money(qPaid || qEarned)}
                    </span>
                  </th>
                );
              })}
              <th>Year</th>
            </tr>
            <tr>
              <th></th>
              {YEAR_INDEXES.map((i) => (
                <th key={i} class={monthClass(i)}>{MONTH_NAMES[i]}</th>
              ))}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <Section label="Sales closing" cols={14} />
            <TierRow label="17% to 19% · $125" hit={(e) => e.closingTier === '17'} earned={earned} amount={125} />
            <TierRow label="20% to 24% · $250" hit={(e) => e.closingTier === '20'} earned={earned} amount={250} />
            <TierRow label="25% + · $450" hit={(e) => e.closingTier === '25'} earned={earned} amount={450} />
            <InputRow
              label="Actual closing %"
              canWrite={open}
              months={months}
              read={(m) => m.closingPct}
              show={(m) => m.closingPct == null ? '' : String(m.closingPct)}
              display={(m) => m.closingPct == null ? '—' : `${m.closingPct}%`}
              write={(i, raw) => onDraft(i, { closingPct: numFrom(raw) })}
            />

            <Section label="InMoment NPS" cols={14} />
            <TierRow label="75% to 79% · $100" hit={(e) => e.npsTier === '75'} earned={earned} amount={100} />
            <TierRow label="80+ · $200" hit={(e) => e.npsTier === '80'} earned={earned} amount={200} />
            <InputRow
              label="Actual NPS %"
              canWrite={open}
              months={months}
              read={(m) => m.npsPct}
              show={(m) => m.npsPct == null ? '' : String(m.npsPct)}
              display={(m) => m.npsPct == null ? '—' : `${m.npsPct}%`}
              write={(i, raw) => onDraft(i, { npsPct: numFrom(raw) })}
            />

            <Section label="Active members · $100 if +10" cols={14} />
            <TierRow label="Achieved" hit={(e) => e.membersHit} earned={earned} amount={100} />
            <InputRow
              label="Last month"
              canWrite={open}
              months={months}
              read={(m) => m.membersLast}
              show={(m) => m.membersLast == null ? '' : String(m.membersLast)}
              write={(i, raw) => onDraft(i, { membersLast: numFrom(raw) })}
            />
            <InputRow
              label="Current month"
              canWrite={open}
              months={months}
              read={(m) => m.membersCurrent}
              show={(m) => m.membersCurrent == null ? '' : String(m.membersCurrent)}
              write={(i, raw) => onDraft(i, { membersCurrent: numFrom(raw) })}
            />
            {canWrite ? (
              <FlagRow
                label="Pay members anyway"
                months={months}
                read={(m) => m.membersOverride}
                write={(i, on) => onDraft(i, { membersOverride: on })}
                canWrite={open}
              />
            ) : null}

            <Section label={`Service hours · $100 if up and ${floor.toLocaleString('en-US')}+`} cols={14} />
            <TierRow label="Achieved" hit={(e) => e.hoursHit} earned={earned} amount={100} />
            <InputRow
              label="Last month"
              canWrite={open}
              months={months}
              read={(m) => m.hoursLast}
              show={(m) => m.hoursLast == null ? '' : String(m.hoursLast)}
              write={(i, raw) => onDraft(i, { hoursLast: numFrom(raw) })}
            />
            <InputRow
              label="Current month"
              canWrite={open}
              months={months}
              read={(m) => m.hoursCurrent}
              show={(m) => m.hoursCurrent == null ? '' : String(m.hoursCurrent)}
              write={(i, raw) => onDraft(i, { hoursCurrent: numFrom(raw) })}
            />
            {canWrite ? (
              <FlagRow
                label="Pay hours anyway"
                months={months}
                read={(m) => m.hoursOverride}
                write={(i, on) => onDraft(i, { hoursOverride: on })}
                canWrite={open}
              />
            ) : null}

            <Section label="Tasks Board Items · $50" cols={14} />
            <TierRow label="Achieved" hit={(e) => e.mondayHit} earned={earned} amount={50} />
            <FlagRow
              label="All items completed?"
              months={months}
              read={(m) => m.mondayDone}
              write={(i, on) => onDraft(i, { mondayDone: on })}
              canWrite={open}
            />

            <Section label="Hiring / staffing · $100" cols={14} />
            <TierRow label="Achieved" hit={(e) => e.hiringHit} earned={earned} amount={100} />
            <FlagRow
              label="Staffed and hiring done?"
              months={months}
              read={(m) => m.hiringOk}
              write={(i, on) => onDraft(i, { hiringOk: on })}
              canWrite={open}
            />

            <Section label="Owner discretionary · up to $100" cols={14} />
            <InputRow
              label="Amount paid"
              canWrite={open}
              months={months}
              read={(m) => m.discretionary}
              show={(m) => m.discretionary == null ? '' : String(m.discretionary)}
              display={(m) => money(m.discretionary)}
              write={(i, raw) => onDraft(i, { discretionary: numFrom(raw) })}
            />

            <Section label="BEST visit · 90%+, $250 max a quarter" cols={14} />
            <InputRow
              label="Score %"
              canWrite={open}
              months={months}
              read={(m) => m.bestScore}
              show={(m) => m.bestScore == null ? '' : String(m.bestScore)}
              display={(m) => m.bestScore == null ? '—' : `${m.bestScore}%`}
              write={(i, raw) => onDraft(i, { bestScore: numFrom(raw) })}
            />
            <InputRow
              label="Amount paid"
              canWrite={open}
              months={months}
              read={(m) => m.bestPaid}
              show={(m) => m.bestPaid == null ? '' : String(m.bestPaid)}
              display={(m) => money(m.bestPaid)}
              write={(i, raw) => onDraft(i, { bestPaid: numFrom(raw) })}
            />

            <Section label="Dec GS sales · 80% $100, 100% $200" cols={14} />
            <InputRow
              label="GS sales %"
              canWrite={open}
              months={months}
              read={(m) => m.gsSalesPct}
              show={(m) => m.gsSalesPct == null ? '' : String(m.gsSalesPct)}
              display={(m) => m.gsSalesPct == null ? '—' : `${m.gsSalesPct}%`}
              write={(i, raw) => onDraft(i, { gsSalesPct: numFrom(raw) })}
            />

            <tr>
              <td>Earned from the numbers</td>
              {YEAR_INDEXES.map((i) => (
                <td
                  key={i}
                  class={`${monthClass(i)}${earned[i].total ? ' good' : ''}`}
                  data-testid={`bonus-earned-${i + 1}`}
                >
                  {earned[i].total ? money(earned[i].total) : '—'}
                </td>
              ))}
              <td class="good">{money(yearEarned)}</td>
            </tr>
            <InputRow
              label="Actual bonus paid"
              canWrite={open}
              months={months}
              read={(m) => m.actualPaid}
              show={(m) => m.actualPaid == null ? '' : String(m.actualPaid)}
              display={(m) => money(m.actualPaid)}
              write={(i, raw) => onDraft(i, { actualPaid: numFrom(raw) })}
              year={money(yearPaid)}
              testid="bonus-paid"
            />
            <ManagerEntryRow clinic={clinic} year={year} months={savedMonths} plan={plan} editing={editing} />
          </tbody>
        </table>
      </div>
      <div class="bonus-legend">
        <h3>How it is calculated</h3>
        <ul>
          <li>
            Sales Closing: Determined by the overall membership sales closing
            percentage for the location at the end of each month. Sales closing
            consists of total membership sales divided by the number of total
            guests. Compensation will be tiered: as each percentage goal is met,
            appropriate compensation will be given. Minimums of 12, 25 and 45
            memberships must be sold to qualify for the three levels respectively.
            17% to 19% pays $125, 20% to 24% pays $250, 25%+ pays $450.
            Percentage scores of .5% or above will be rounded up.
          </li>
          <li>
            InMoment NPS Score: 100% Resolution Rate for all Callback Alert
            surveys must be achieved to receive this bonus. 75% to 79% pays $100.
            80+ pays $200.
          </li>
          <li>
            Active Member Increase: In order for this bonus to be paid, the
            number of active members must increase by 10 month over month. Pays $100.
          </li>
          <li>
            Service Hour Increase: Hours must be higher than last month and at
            or above that clinic’s minimum. Lake Mary is 1,000. The Villages
            is 1,400. Winter Garden is 1,700. This clinic’s minimum is{' '}
            {floor.toLocaleString('en-US')}. Pays $100.
          </li>
          <li>
            Tasks Board Items: All Tasks Board Items must be completed properly,
            per guidelines, by specified due dates. Pays $50.
          </li>
          <li>
            Staffing/Hiring Bonus: For this bonus to be paid, the location must
            be properly staffed for the full month at the Front Desk. Also, all
            SPs should be managed properly throughout the hiring process. This
            means ensuring all candidates are logged into and updated in
            FreshTeams, and you are onboarding them in a timely manner. This
            will be discretionary and managed by Tomer. If he must request
            status updates, more than likely, this bonus will not be paid for
            the month. Pays $100.
          </li>
          <li>
            BEST visit: Score must be over 90% to receive bonus. The max paid
            for BEST VISITs is $250 per quarter, regardless of the number of
            BEST VISITs conducted. Meaning if you have more than one BEST VISIT
            in one quarter (due to being in the 2nd half of the ME averages) and
            score above 90% or higher on one or both, the BEST VISIT bonus will
            be $250 max. Regardless of BEST VISIT score, if any critical default
            items are found, no BEST VISIT bonus will be paid.
          </li>
          <li>
            Terms and Conditions: Business Manager will forfeit all unpaid
            monthly bonuses if any formal disciplinary action is taken during
            the bonus month, or in the period preceding the payroll for that
            bonus month; InMoment Surveys are not emailed to owners and
            addressed in a timely fashion; LMT and EST candidates are not
            properly logged on ATS AND FreshTeams; Tasks Board Items are not
            properly completed; not adhering to max allowable hours allotted
            for Front Desk Staff; not submitting a Wufoo form for any Customer
            Service points given to members and guests; the location receives a
            notice of default for any reason within the control of the manager;
            or full-time status of 80 hours per payroll period is not
            maintained. Loss of time, excessive tardiness, or absences will
            result in disqualification.
          </li>
        </ul>
      </div>
    </div>
  );
}

function Section({ label, cols }: { label: string; cols: number }): JSX.Element {
  return (
    <tr class="bonus-section">
      <td colSpan={cols}>{label}</td>
    </tr>
  );
}

function TierRow(
  { label, hit, earned, amount, indexes = YEAR_INDEXES }:
  {
    label: string;
    hit: (e: ReturnType<typeof earnMonth>) => boolean;
    earned: ReturnType<typeof earnMonth>[];
    amount: number;
    indexes?: number[];
  },
): JSX.Element {
  const slice = indexes.map((i) => earned[i]);
  return (
    <tr>
      <td>{label}</td>
      {slice.map((e, n) => (
        <td key={indexes[n]} class={`${monthClass(indexes[n])}${hit(e) ? ' good' : ''}`}>
          {hit(e) ? money(amount) : '—'}
        </td>
      ))}
      <td>{money(slice.filter(hit).length * amount)}</td>
    </tr>
  );
}

/** "Actual bonus paid" → payroll (owner, 2026-10-06: the tracker had no
 * connection to Manager Entry). Each month's paid amount goes on Manager Entry
 * as a BONUS line "Manager bonus MM/YYYY" for the clinic's manager, on the pay
 * period holding the month's last day — through Manager Entry's own submit and
 * void routes and guards. Only the owner sends; everyone else sees the state. */
function ManagerEntryRow(
  { clinic, year, months, plan, editing }:
  { clinic: string; year: number; months: BonusMonth[]; plan?: BonusPlan; editing: boolean },
): JSX.Element {
  const me = session.value.me;
  const isOwner = me?.role === 'OWNER' && !me?.viewingAs;
  const [busy, setBusy] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const manager = plan?.manager ?? null;

  async function send(i: number): Promise<void> {
    const link = plan?.payroll?.[i];
    const paid = months[i].actualPaid ?? 0;
    const state = bonusSendState(months[i].actualPaid, link);
    if (!link?.bonusPayroll || !manager) return;
    const key = crypto.randomUUID();
    setBusy(i);
    setError(null);
    setNote(null);
    try {
      if ((state === 'changed' || state === 'remove') && link.sent) {
        await voidEntryLine('earnings', link.sent.lineId, `${key}:void`);
      }
      if (state === 'ready' || state === 'changed') {
        await submitManagerEntry({
          locationCode: clinic,
          payPeriodId: link.bonusPayroll.payPeriodId,
          entries: { bonus: [{ employeeId: manager.employeeId, amount: paid, detail: link.reason }] },
        }, `${key}:submit`);
      }
      await refetchResource(bonusTrackerKey(year), () => fetchBonusTracker(year));
      setNote(state === 'remove'
        ? `${MONTH_NAMES[i]}: removed from Manager Entry (Week ${link.bonusPayroll.adpWeek}).`
        : `${MONTH_NAMES[i]}: ${money(paid)} for ${manager.name} ${state === 'changed' ? 'updated on' : 'added to'} Manager Entry, Week ${link.bonusPayroll.adpWeek}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <tr data-testid="bonus-manager-entry">
        <td>Manager Entry</td>
        {YEAR_INDEXES.map((i) => {
          const link = plan?.payroll?.[i];
          const state = bonusSendState(months[i].actualPaid, link);
          const wk = link?.bonusPayroll?.adpWeek;
          let cell: JSX.Element | string = '—';
          if (state === 'onPayroll') {
            // Submitted = Closed (owner, 2026-10-06). To change it, Edit the
            // tracker at the top: a new "Actual bonus paid" offers Update, $0
            // offers Remove.
            cell = <span class="muted" data-testid={`bonus-closed-${i + 1}`} title={`Submitted to Manager Entry, Week ${wk}`}>Closed</span>;
          } else if (state !== 'none') {
            const closed = !link?.bonusPayroll || link.bonusPayroll.closed;
            const label = state === 'ready' ? 'Submit'
              : state === 'changed' ? `Update to ${money(months[i].actualPaid)}` : 'Remove';
            if (!isOwner) {
              cell = <span class="badge" title="Tomer sends it to Manager Entry">{state === 'ready' ? 'Ready' : 'Changed'}</span>;
            } else if (closed && state === 'ready') {
              // A closed paycheck with nothing sent: paid outside the tracker
              // (before 2026-10-06) or not at all — never offered as "Send".
              cell = <span class="muted" title="That paycheck is closed">Closed</span>;
            } else if (!manager || closed || editing) {
              cell = <span class="muted" title={!manager ? (plan?.managerProblem ?? '') : closed ? 'Pay period closed' : 'Save first'}>{label}</span>;
            } else {
              cell = (
                <button
                  type="button" class="btn small" disabled={busy !== null}
                  data-testid={`bonus-send-${i + 1}`}
                  title={`${manager.name} — Week ${wk}`}
                  onClick={() => { void send(i); }}
                >
                  {busy === i ? '…' : label}
                </button>
              );
            }
          }
          return <td key={i} class={monthClass(i)}>{cell}</td>;
        })}
        <td>—</td>
      </tr>
      {(isOwner && !manager && plan?.managerProblem) || note || error ? (
        <tr>
          <td colSpan={14}>
            {isOwner && !manager && plan?.managerProblem ? <small class="bad" role="alert">{plan.managerProblem}</small> : null}
            {note ? <small role="status" data-testid="bonus-send-note">{note}</small> : null}
            {error ? <small class="bad" role="alert" data-testid="bonus-send-error">{error}</small> : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function InputRow(
  { label, canWrite, months, indexes = YEAR_INDEXES, read, show, display, write, commit, year, testid }:
  {
    label: string;
    canWrite: boolean;
    months: BonusMonth[];
    indexes?: number[];
    read: (m: BonusMonth) => number | null;
    show: (m: BonusMonth) => string;
    display?: (m: BonusMonth) => string;
    write: (i: number, raw: string) => void;
    commit?: (i: number) => void;
    year?: string;
    testid?: string;
  },
): JSX.Element {
  return (
    <tr>
      <td>{label}</td>
      {indexes.map((i) => {
        const m = months[i];
        return (
          <td key={i} class={monthClass(i)}>
            {canWrite ? (
              <input
                class="row-edit"
                inputMode="decimal"
                aria-label={`${label} ${MONTH_NAMES[i]}`}
                data-testid={testid ? `${testid}-${i + 1}` : undefined}
                value={show(m)}
                onInput={(e) => write(i, (e.target as HTMLInputElement).value)}
                onBlur={() => commit?.(i)}
              />
            ) : (
              <span data-testid={testid ? `${testid}-${i + 1}` : undefined}>
                {display ? display(m) : (read(m) == null ? '—' : show(m))}
              </span>
            )}
          </td>
        );
      })}
      <td>{year ?? '—'}</td>
    </tr>
  );
}

function FlagRow(
  { label, months, indexes = YEAR_INDEXES, read, write, canWrite = true }:
  {
    label: string;
    months: BonusMonth[];
    indexes?: number[];
    read: (m: BonusMonth) => boolean;
    write: (i: number, on: boolean) => void;
    canWrite?: boolean;
  },
): JSX.Element {
  return (
    <tr>
      <td>{label}</td>
      {indexes.map((i) => (
        <td key={i} class={monthClass(i)}>
          {canWrite ? (
            <input
              type="checkbox"
              aria-label={`${label} ${MONTH_NAMES[i]}`}
              checked={read(months[i])}
              onChange={(e) => write(i, (e.target as HTMLInputElement).checked)}
            />
          ) : (read(months[i]) ? 'Y' : 'N')}
        </td>
      ))}
      <td>—</td>
    </tr>
  );
}
